import GLOBAL from '../configs/config_global';
import moment from 'moment-timezone';
import { deleteCalendarEvent } from '../modules';
import { showAppError } from './util_helpers';
import { getConvertedTimezone, toCurrentDeviceTime } from '../modules/converters';
import { showAlert } from './util_helpers';

// Moment Setup
const momentDurationFormatSetup = require('moment-duration-format');
momentDurationFormatSetup(moment);

// region Prompt to Delete Event
export function isRecurringEvent(eventData) {
    const originalStartTime = toCurrentDeviceTime(eventData['start_time'], getConvertedTimezone(eventData['timezone'])).unix();
    return((originalStartTime !== eventData.timeObj.startTime) && eventData.timeObjArr && eventData.timeObjArr.length > 1);
}

export function promptRemoveEvent({
    eventData = {},
    onDeletePressed = () => {},
    onDeleteCompleted = () => {},
    onDeleteError = () => {}
}) {
    const hasMultipleInstances = eventData.timeObjArr?.length > 1;

    const executeDelete = (type) => {
        onDeletePressed();
        deleteCalendarEvent(eventData.id, eventData.timeObj.utcDateStr, type)
            .then((newEventResult) => onDeleteCompleted(newEventResult, type))
            .catch((error) => {
                onDeleteError(error);
                showAppError(error);
            });
    }

    const deleteFunc = (type = 3) => {
        if (hasMultipleInstances) {
            showAlert({
                text: `Confirm deleting <b>${type === 1 ? 'only this event' : (type === 2 ? 'this and following events' : 'all events')}</b>?`,
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    color: 'red',
                    text: 'Confirm',
                    onPress: () => executeDelete(type)
                }
            })
        }
        else {
            executeDelete(3);
        }
    };

    showAlert(Object.assign(
        {
            text: `Are you sure you want to remove this event from your calendar?<br/><br/><b>${eventData['location_name']}</b><br/><i>${moment.unix(eventData.timeObj.startTime).format('h:mm A, MMM D, YYYY')}</i>` + (
                hasMultipleInstances ? '<br/><br/>This event is part of a recurring series, choose your removal option below' : ''
            )
        },
        hasMultipleInstances ?
            {
                confirmButton: {
                    text: 'Only this event',
                    onPress: () => deleteFunc(1)
                },
                thirdButton: {
                    text: 'This and following events',
                    onPress: () => deleteFunc(2)
                },
                cancelButton: {
                    text: 'All Events',
                    onPress: () => deleteFunc(3)
                }
            }
            :
            {
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    color: GLOBAL.COLOR.MAIN,
                    text: 'Delete',
                    onPress: () => deleteFunc(3)
                }
            }
    ));
}
// endregion

// region Add Event to Calendar
function getRandomKey() {
    const n = Math.floor(Math.random() * 999999999999).toString();
    return new Date().getTime().toString() + '_' + n;
}

function formatTimeForCalendar(date, useLocal = false, parseUTC = true) {
    const timeFormats = {
        dateTimeLocal: 'YYYY-MM-DD[T]HH:mm:ss',
        dateTimeUTC: 'YYYYMMDD[T]HHmmss[Z]'
    };
    return(
        (parseUTC ? moment.unix(date).utc() : moment.unix(date)).format(useLocal ? timeFormats.dateTimeLocal : timeFormats.dateTimeUTC)
    );
}

function calculateDuration(startTime, endTime) {
    const difference = moment.unix(endTime).diff(moment.unix(startTime), 'seconds');
    return moment.duration(difference, 'seconds').format('hh:mm');
}

export function generateCalendarURL(event, type = 'google') {
    let calendarUrl = '';

    // allow mobile browsers to open the gmail data URI within native calendar app
    // type = (type == "google" && this.isMobile()) ? "outlook" : type;

    // startTime & endTime: UTC Unix

    switch(type) {
        case 'google': {
            calendarUrl = 'https://calendar.google.com/calendar/render';
            calendarUrl += '?action=TEMPLATE';
            calendarUrl += '&dates=' + formatTimeForCalendar(event.startTime);
            calendarUrl += '/' + formatTimeForCalendar(event.endTime);
            calendarUrl += '&location=' + encodeURIComponent(event.location);
            calendarUrl += '&text=' + encodeURIComponent(event.title);
            calendarUrl += '&details=' + encodeURIComponent(event.description);
            break;
        }
        case 'yahoo': {
            // yahoo doesn't utilize endTime so we need to calculate duration
            calendarUrl = 'https://calendar.yahoo.com/?v=60&view=d&type=20';
            calendarUrl += '&title=' + encodeURIComponent(event.title);
            calendarUrl += '&st=' + formatTimeForCalendar(event.startTime, false, false);
            calendarUrl += '&dur=' + calculateDuration(event.startTime, event.endTime);
            calendarUrl += '&desc=' + encodeURIComponent(event.description);
            calendarUrl += '&in_loc=' + encodeURIComponent(event.location);
            break;
        }
        case 'outlook': {
            calendarUrl = 'https://outlook.live.com/owa/?rru=addevent';
            calendarUrl += '&startdt=' + formatTimeForCalendar(event.startTime, true, false);
            calendarUrl += '&enddt=' + formatTimeForCalendar(event.endTime, true, false);
            calendarUrl += '&subject=' + encodeURIComponent(event.title);
            calendarUrl += '&location=' + encodeURIComponent(event.location);
            calendarUrl += '&body=' + encodeURIComponent(event.description);
            calendarUrl += '&allday=false';
            calendarUrl += '&uid=' + getRandomKey();
            calendarUrl += '&path=/calendar/view/Month';
            break;
        }
        default: {
            calendarUrl = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'BEGIN:VEVENT',
                'URL:' + document.URL,
                'DTSTART:' + formatTimeForCalendar(event.startTime),
                'DTEND:' + formatTimeForCalendar(event.endTime),
                'SUMMARY:' + event.title,
                'DESCRIPTION:' + event.description,
                'LOCATION:' + event.location,
                'END:VEVENT',
                'END:VCALENDAR'
            ].join('\n');
        }
    }

    return calendarUrl;
}

export function addEventToCalendar(event, type = 'google') {
    const url = generateCalendarURL(event, type);

    if (url.startsWith('BEGIN')) {
        const filename = `truckster_event_${moment.unix(event.startTime).format('YYYYMMDD')}.ics`;
        let blob = new Blob([url], { type: 'text/calendar;charset=utf-8' });

        /****************************************************************
         // many browsers do not properly support downloading data URIs
         // (even with "download" attribute in use) so this solution
         // ensures the event will download cross-browser
         ****************************************************************/
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    else {
        window.open(url, '_blank');
    }
}

export function promptToAddToCalendar(event) {
    showAlert({
        text: `Add this event to your calendar so you won't forget?<br/><br/><b>${event['title']}</b><br/><i>${moment.unix(event.startTime).format('h:mm A, MMM D, YYYY')}</i><br/><br/>Choose the calendar you want to add this event to:`,
        confirmButton: {
            text: 'Outlook',
            onPress: () => addEventToCalendar(event, 'outlook')
        },
        thirdButton: {
            text: 'Google',
            onPress: () => addEventToCalendar(event, 'google')
        },
        cancelButton: {
            text: 'Cancel'
        }
    })
}
// endregion
